import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VideoJsPlayer } from 'video.js';

import { useCanPlay } from './use-can-play';
import { sToMs } from '../../../utils/time';
import { SubtitleOption } from '../../settings-menu/settings-menu.types';
import { PlayerTextTrack } from '../player.config';
import { Transcript } from '../player.interface';
import { setMinimumDisplayTime } from '../utils/parse-data-cues';
import { VideoJsNativeEvent } from '../videojs-event';

export interface Subtitle {
  text: string;
}

// activeTrack mode: disabled, or not set -> video doesnt dispatch cuechange events
// activeTrack mode: showing -> video dispatch cuechange events

export function useSubtitles(
  player: VideoJsPlayer,
  playerLanguage: string | undefined,
  preferredBrowserLanguages: ReadonlyArray<string>,
  trackOptions: PlayerTextTrack[] | undefined,
  selectedSubtitleId = '',
  subtitleEnabled: boolean,
  subtitleFallbackLanguage: string,
  setSelectedSubtitleId: Dispatch<SetStateAction<string>>,
  selectedTranscriptId?: string,
  setSelectedTranscriptId?: Dispatch<SetStateAction<string>>
): {
  subtitle: Subtitle | null;
  subtitleOptions: SubtitleOption[];
  transcript: Transcript | null;
  transcriptOptions: SubtitleOption[];
} {
  const [subtitle, setSubtitle] = useState<Subtitle | null>(null);
  const [subtitleOptions, setSubtitleOptions] = useState<SubtitleOption[]>([]);
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [transcriptOptions, setTranscriptOptions] = useState<SubtitleOption[]>([]);
  const canPlay = useCanPlay(player);
  const { i18n } = useTranslation();
  const minDisplayTime = 2;

  // Detect when subtitles tracks are added/removed from the player
  useEffect(() => {
    const onAddTrack = ({ track }: TrackEvent) => {
      if (isValidSubtitleTrack(track)) {
        // Add track to the state, if it does not exist yet
        setSubtitleOptions((prevState) =>
          prevState.find((t) => t.id === track.id) ? prevState : [...prevState, mapTextTrackToSubtitleOption(track)]
        );

        if (track.kind === 'captions') {
          track.mode = 'hidden'; // Set captions to hidden by default
          const playerOptionsTrack = trackOptions?.find((t) => t.id === track.id);

          if (playerOptionsTrack) {
            setTranscriptOptions((prevState) =>
              prevState.find((t) => t.id === track.id)
                ? prevState
                : [...prevState, mapTextTrackToTranscriptOption(track, playerOptionsTrack)]
            );
          }
        }
      }
    };

    const onRemoveTrack = ({ track }: TrackEvent) => {
      if (isValidSubtitleTrack(track)) {
        // Remove the track from the state
        setSubtitleOptions((prevState) => prevState.filter((t) => t.id !== track.id));
        setTranscriptOptions((prevState) => prevState.filter((t) => t.id !== track.id));

        // If the removed track was the selected one, set the selected track to off
        setSelectedSubtitleId((prevState) => (prevState === track.id ? '' : prevState));

        const initialSelectedTrack = getInitialSelectedTrack(
          Array.from(player.textTracks()).filter((t) => t.kind === 'captions'),
          preferredBrowserLanguages as string[],
          subtitleFallbackLanguage,
          playerLanguage,
          i18n.resolvedLanguage
        );
        setSelectedTranscriptId?.(initialSelectedTrack?.id || '');
      }
    };

    // Add 'addtrack' and 'removetrack' event listeners
    player.textTracks().addEventListener(VideoJsNativeEvent.ADD_TRACK, onAddTrack);
    player.textTracks().addEventListener(VideoJsNativeEvent.REMOVE_TRACK, onRemoveTrack);

    return () => {
      player.textTracks().removeEventListener(VideoJsNativeEvent.ADD_TRACK, onAddTrack);
      player.textTracks().removeEventListener(VideoJsNativeEvent.REMOVE_TRACK, onRemoveTrack);
    };
  }, [
    i18n.resolvedLanguage,
    player,
    playerLanguage,
    preferredBrowserLanguages,
    setSelectedSubtitleId,
    setSelectedTranscriptId,
    subtitleFallbackLanguage,
    trackOptions,
  ]);

  // Add external subtitles to player
  useEffect(() => {
    if (trackOptions?.length) {
      const addedTracks: HTMLTrackElement[] = [];
      const setExternalSubtitles = () => {
        const tracks = Array.from(player.textTracks());

        trackOptions.filter(isSubtitleOrCaptions).forEach((trackOption) => {
          // If the track is not added yet, add it to the player
          const loadedTrack = getPlayerTrackById(tracks, trackOption.id);
          if (!loadedTrack) {
            const newTrack =
              trackOption.kind === 'captions'
                ? player.addRemoteTextTrack({ ...trackOption, mode: 'hidden' }, true)
                : player.addRemoteTextTrack(trackOption, true);
            addedTracks.push(newTrack);
          }
        });
      };

      player.on(VideoJsNativeEvent.LOADED_METADATA, setExternalSubtitles);

      return () => {
        addedTracks.forEach((track) => player.removeRemoteTextTrack(track));

        player.off(VideoJsNativeEvent.LOADED_METADATA, setExternalSubtitles);
      };
    }
  }, [player, trackOptions]);

  // Set the default subtitle language
  useEffect(() => {
    if (!canPlay || !player) return;

    if (!selectedSubtitleId && subtitleEnabled) {
      const tracks = Array.from(player.textTracks()).filter(isValidSubtitleTrack);

      if (tracks.length > 0) {
        const initialSelectedTrack = getInitialSelectedTrack(
          tracks,
          preferredBrowserLanguages as string[],
          subtitleFallbackLanguage,
          playerLanguage,
          i18n.resolvedLanguage
        );
        if (initialSelectedTrack) {
          setSelectedSubtitleId(initialSelectedTrack.id);
        }
      }
    }
  }, [
    player,
    playerLanguage,
    preferredBrowserLanguages,
    i18n,
    canPlay,
    subtitleEnabled,
    trackOptions,
    selectedSubtitleId,
    subtitleFallbackLanguage,
    setSelectedSubtitleId,
  ]);

  // Activate the current selected subtitle (and deactivate the previous one) when selectedSubtitleId changes
  useEffect(() => {
    if (!canPlay || !player) return;

    if (selectedSubtitleId && selectedSubtitleId !== 'off') {
      const trackToActivate = Array.from(player.textTracks()).find(
        (trackOption) => trackOption.id === selectedSubtitleId
      );

      if (trackToActivate) {
        const onCueChange = () => {
          setMinimumDisplayTime(trackToActivate, minDisplayTime);
          setSubtitleByActiveCue(trackToActivate);
        };

        // Activate the selected track
        trackToActivate.mode = 'hidden';
        trackToActivate.addEventListener(VideoJsNativeEvent.CUE_CHANGE, onCueChange);

        setMinimumDisplayTime(trackToActivate, minDisplayTime);

        // Read currently active cue
        setSubtitleByActiveCue(trackToActivate);

        return () => {
          // Deactivate previous track
          trackToActivate.mode = trackToActivate.kind === 'captions' ? 'hidden' : 'disabled';
          trackToActivate.removeEventListener(VideoJsNativeEvent.CUE_CHANGE, onCueChange);
        };
      }
    } else {
      setSubtitle(null);
    }

    function setSubtitleByActiveCue(track: TextTrack) {
      if (track && track.activeCues?.length) {
        const activeCue = track.activeCues[0] as VTTCue;

        if (activeCue) {
          setSubtitle({ text: activeCue.text });
        } else {
          setSubtitle(null);
        }
      } else {
        setSubtitle(null);
      }
    }
  }, [player, canPlay, trackOptions, selectedSubtitleId]);

  /* Transcripts */

  // Load transcript when it is ready. This is done like this so we don't have to activate the track to get the cues (on cue_change event)
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (selectedTranscriptId && (!transcript || transcript.id !== selectedTranscriptId)) {
      // Try to read immediately (if this is already loaded)
      const newTranscript = readTranscript(player, selectedTranscriptId);
      if (newTranscript && newTranscript.cues.length > 0) {
        setTranscript(newTranscript);
      } else {
        // If not, lets ping the tracks until it is
        interval = setInterval(() => {
          const newTranscript = readTranscript(player, selectedTranscriptId);
          if (newTranscript && newTranscript.cues.length > 0) {
            setTranscript(newTranscript);
            clearInterval(interval);
          }
        }, 100);
      }
    }

    return () => clearInterval(interval);
  }, [player, selectedTranscriptId, transcript]);

  // Select the default transcript language (if available)
  useEffect(() => {
    if (!selectedTranscriptId && transcriptOptions.length > 0) {
      const initialSelectedTrack = getInitialSelectedTrack(
        Array.from(player.textTracks()).filter((t) => t.kind === 'captions'),
        preferredBrowserLanguages as string[],
        subtitleFallbackLanguage,
        playerLanguage,
        i18n.resolvedLanguage
      );
      setSelectedTranscriptId?.(initialSelectedTrack?.id || '');
    }
  }, [
    i18n.resolvedLanguage,
    player,
    playerLanguage,
    preferredBrowserLanguages,
    selectedTranscriptId,
    setSelectedTranscriptId,
    subtitleFallbackLanguage,
    transcriptOptions,
  ]);

  return { subtitle, subtitleOptions, transcript, transcriptOptions };
}

// we want to handle captions the same way as subtitles
const isSubtitleOrCaptions = (track: PlayerTextTrack) => track.kind === 'subtitles' || track.kind === 'captions';

const getPlayerTrackById = (tracks: TextTrack[], id = ''): TextTrack | undefined => {
  return tracks.find((track) => isValidSubtitleTrack(track) && track.id === id);
};

const isValidSubtitleTrack = (track: TextTrack | null): track is TextTrack => {
  return !!(
    track &&
    track.id &&
    track.language &&
    track.label &&
    (track.kind === 'subtitles' || track.kind === 'captions')
  );
};

const mapTextTrackToSubtitleOption = (track: TextTrack): SubtitleOption => {
  return {
    id: track.id,
    languageCode: track.language,
    englishLabel: track.label,
    kind: track.kind,
  };
};

const mapTextTrackToTranscriptOption = (track: TextTrack, playerOptionsTrack?: PlayerTextTrack): SubtitleOption => {
  return {
    id: track.id,
    languageCode: track.language,
    englishLabel: track.language?.substring(0, 2).toUpperCase(),
    src: playerOptionsTrack?.src,
  };
};

const mapTrackToTranscript = (track: TextTrack): Transcript => {
  return {
    id: track.id,
    language: track.language,
    label: track.label,
    cues: Array.from(track.cues || []).flatMap((cue) => {
      const vttCue = cue as VTTCue;
      // the player dispatch the time in milliseconds, so we convert the time
      return { text: vttCue.text, startTime: sToMs(vttCue.startTime), endTime: sToMs(vttCue.endTime) };
    }),
  };
};

const readTranscript = (player: VideoJsPlayer, selectedTranscriptId: string) => {
  const transcriptTrack = Array.from(player.textTracks()).find((track) => track.id === selectedTranscriptId);
  return transcriptTrack ? mapTrackToTranscript(transcriptTrack) : null;
};

const findTrack = (tracks: TextTrack[], language: string): TextTrack | undefined => {
  if (!language || typeof language !== 'string') return;

  const preferredLanguage = language.toLowerCase();
  let track = tracks.find((track) => track.language.toLowerCase() === preferredLanguage);

  if (!track) {
    const fallbackLanguage = preferredLanguage.substring(0, 2);

    track = tracks.find((track) => track.language.toLowerCase() === fallbackLanguage);
  }

  return track;
};

const getInitialSelectedTrack = (
  tracks: TextTrack[],
  preferredBrowserLanguages: string[],
  subtitleFallbackLanguage?: string,
  playerLanguage?: string,
  resolvedLanguage?: string
) => {
  let track: TextTrack | undefined;

  // Priority filters to get the best track available based on the preferred languages
  const priorityFilters = [
    (tracks: TextTrack[]) => tracks.filter((t) => t.kind === 'subtitles'),
    (tracks: TextTrack[]) => tracks.filter((t) => t.kind === 'captions'),
  ];

  for (let i = 0; i < priorityFilters.length; i++) {
    const filteredTracks = priorityFilters[i](tracks);
    // 1. try to match preferred browser languages from top to bottom
    if (preferredBrowserLanguages.length > 0) {
      for (const preferredBrowserLanguage of preferredBrowserLanguages) {
        track = findTrack(filteredTracks, preferredBrowserLanguage);

        if (track) {
          return track;
        }
      }
    }

    // 2. try to fallback to the configured fallback language
    if (subtitleFallbackLanguage) {
      track = findTrack(filteredTracks, subtitleFallbackLanguage);

      if (track) {
        return track;
      }
    }

    // 3. try to fallback to the player language
    if (playerLanguage) {
      track = findTrack(filteredTracks, playerLanguage);

      if (track) {
        return track;
      }
    }

    // 4. try to fallback to the player resolved language
    if (resolvedLanguage) {
      track = findTrack(filteredTracks, resolvedLanguage);

      if (track) {
        return track;
      }
    }
  }

  // Last resort. try to fallback to the first available subtitle
  if (tracks.length > 0) {
    return tracks[0];
  }

  return undefined;
};
